/** Query / facets / folders / tags inputs plus the relevance-vs-latest sort toggle. */

import type { SearchSort } from "../api/types";
import { el } from "../lib/dom";
import { parseTagInput } from "../lib/tagInput";

export interface SearchFormValue {
  readonly query: string;
  readonly facets: string;
  readonly tags: readonly string[];
  readonly folders: readonly string[];
  readonly sort: SearchSort;
}

export interface SearchFormOptions {
  readonly initial: SearchFormValue;
  readonly onSubmit: (value: SearchFormValue) => void;
}

export interface SearchFormHandle {
  readonly element: HTMLElement;
  readonly getValue: () => SearchFormValue;
  readonly setTags: (tags: readonly string[]) => void;
  readonly setFolders: (folders: readonly string[]) => void;
}

const SORT_OPTIONS: readonly { readonly value: SearchSort; readonly label: string }[] = [
  { value: "relevance", label: "Relevance" },
  { value: "latest", label: "Latest" },
];

function field(label: string, hint: string | HTMLElement, control: HTMLElement, area: string): HTMLElement {
  const id = `field-${area}`;
  control.id = id;
  return el("div", {
    className: "field",
    dataset: { area },
    children: [
      el("label", { className: "field__label", attrs: { for: id }, text: label }),
      control,
      typeof hint === "string" ? el("p", { className: "field__hint", text: hint }) : hint,
    ],
  });
}

/**
 * The query syntax, shown under the box. It mirrors the server's
 * internal/query package one-to-one; change one, change the other. Written as
 * examples rather than a grammar because that is how people read search help.
 */
function querySyntaxHint(): HTMLElement {
  const examples: readonly (readonly [string, string])[] = [
    ["dropbox sync", "all words"],
    ['"dropbox sync"', "exact phrase"],
    ["dropbox or sync", "either"],
    ["-sync", "exclude"],
    ["drop*", "prefix"],
    ["title:sync", "one field; also body:, keywords:"],
  ];
  // Each example and its meaning are one non-breaking unit so a line wrap
  // never separates them; the separators are where the line may break.
  return el("p", {
    className: "field__hint field__hint--syntax",
    children: examples.flatMap(([example, meaning], index) => [
      ...(index === 0 ? [] : [" · "]),
      el("span", {
        className: "field__hint-example",
        children: [el("code", { text: example }), ` ${meaning}`],
      }),
    ]),
  });
}

export function searchForm(options: SearchFormOptions): SearchFormHandle {
  let sort: SearchSort = options.initial.sort;

  const queryInput = el("input", {
    className: "input",
    attrs: { type: "search", name: "query", placeholder: "words to match", autocomplete: "off" },
  });
  queryInput.value = options.initial.query;

  const facetsInput = el("input", {
    className: "input",
    attrs: { type: "search", name: "facets", placeholder: "key:value", autocomplete: "off" },
  });
  facetsInput.value = options.initial.facets;

  const tagsInput = el("input", {
    className: "input",
    attrs: { type: "search", name: "tags", placeholder: "comma separated", autocomplete: "off" },
  });
  tagsInput.value = options.initial.tags.join(", ");

  const foldersInput = el("input", {
    className: "input",
    attrs: { type: "search", name: "folders", placeholder: "comma separated", autocomplete: "off" },
  });
  foldersInput.value = options.initial.folders.join(", ");

  const getValue = (): SearchFormValue => ({
    query: queryInput.value.trim(),
    facets: facetsInput.value.trim(),
    tags: parseTagInput(tagsInput.value),
    folders: parseTagInput(foldersInput.value),
    sort,
  });

  const sortButtons = SORT_OPTIONS.map((option) =>
    el("button", {
      className: `segmented__option${option.value === sort ? " segmented__option--active" : ""}`,
      attrs: { type: "button", "aria-pressed": option.value === sort },
      text: option.label,
    }),
  );

  const applySort = (next: SearchSort): void => {
    sort = next;
    SORT_OPTIONS.forEach((option, index) => {
      const button = sortButtons[index];
      if (button === undefined) {
        return;
      }
      const active = option.value === next;
      button.classList.toggle("segmented__option--active", active);
      button.setAttribute("aria-pressed", String(active));
    });
  };

  SORT_OPTIONS.forEach((option, index) => {
    sortButtons[index]?.addEventListener("click", () => {
      if (option.value === sort) {
        return;
      }
      applySort(option.value);
      options.onSubmit(getValue());
    });
  });

  const sortControl = el("div", {
    className: "segmented",
    attrs: { role: "group", "aria-label": "Sort results" },
    children: sortButtons,
  });

  const form = el("form", {
    className: "search-form",
    attrs: { role: "search" },
    children: [
      field("Query", querySyntaxHint(), queryInput, "query"),
      field("Facets", "Filters the results: every key:value listed must be on the note.", facetsInput, "facets"),
      field("Folder", "Any listed folder; pick from the Folders tab.", foldersInput, "folders"),
      field("Tags", "All listed tags must be present.", tagsInput, "tags"),
      el("div", {
        className: "search-form__controls",
        dataset: { area: "controls" },
        children: [
          el("div", {
            className: "search-form__sort",
            children: [el("span", { className: "field__label", text: "Sort" }), sortControl],
          }),
          el("button", { className: "button button--primary", attrs: { type: "submit" }, text: "Search" }),
        ],
      }),
    ],
  });

  form.addEventListener("submit", (event: SubmitEvent) => {
    event.preventDefault();
    options.onSubmit(getValue());
  });

  return {
    element: form,
    getValue,
    setTags(tags: readonly string[]): void {
      tagsInput.value = tags.join(", ");
    },
    setFolders(folders: readonly string[]): void {
      foldersInput.value = folders.join(", ");
    },
  };
}
